import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSchedulerV3Service, serviceName } from '../service';
import { ListScheduleDefinitionsIO, ServiceQueries } from '../types';
import { useSchedulerV3Queries } from './use-scheduler-v3-queries';
import { UseSchedulerV3QueryEndpointArgs, useSchedulerV3Query } from './use-scheduler-v3-query';

/**
 * A hook that returns a query for the `ListScheduleDefinitions` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListScheduleDefinitionsQuery = <E = unknown, D = ListScheduleDefinitionsIO['output']>(
  args: UseSchedulerV3QueryEndpointArgs<'ListScheduleDefinitions', E, D>
) =>
  useSchedulerV3Query({
    endpointName: 'ListScheduleDefinitions',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListScheduleDefinitions` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListScheduleDefinitionsQueries = <E = unknown, D = ListScheduleDefinitionsIO['output']>(
  argsArr: UseSchedulerV3QueryEndpointArgs<'ListScheduleDefinitions', E, D>[]
) =>
  useSchedulerV3Queries(
    argsArr.map((args) => ({
      endpointName: 'ListScheduleDefinitions',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListScheduleDefinitions` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListScheduleDefinitions = () => {
  const queryClient = useQueryClient();

  const fetchListScheduleDefinitions = async <E = unknown, D = ListScheduleDefinitionsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSchedulerV3QueryEndpointArgs<'ListScheduleDefinitions', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListScheduleDefinitionsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListScheduleDefinitions'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListScheduleDefinitionsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListScheduleDefinitions'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListScheduleDefinitions'>({
          serviceName,
          endpointName: 'ListScheduleDefinitions',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSchedulerV3Service.ListScheduleDefinitions(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListScheduleDefinitions;
};
